// Fades tall cabinet front meshes in/out so the user can see interior shelves.
//
// Only the classified front meshes (Door Left*, Door Right*, Door_Left*, Door_Right*, Drawer*) are faded.
// Their _Cap children have separate materials and remain fully visible throughout.

import SqftStore from "../../../../../../canvas/store/sqftStore";
import createEventManager from "../../../../../../canvas/store/subscriptionEventManager";

const FRONT_RAW_TYPES = new Set(['door_left', 'door_right', 'drawer']);
const MATERIAL_CLONED_TAG = 'doorFadeMaterialCloned';
const ANIM_DURATION_MS = 500;
const DOORS_OPEN_CHANGED_EVENT = 'doorsOpenChanged';

// Opacity fronts reach when "Show Interior" is active (0 = invisible, 1 = fully opaque).
// Adjust this constant to taste — e.g. 0.1 for nearly invisible, 0.3 for frosted-glass look.
export const DOOR_OPEN_OPACITY = 0.1;

let currentRafId = null;
let areDoorsOpen = false;
const eventManager = createEventManager();

function notifyDoorsOpenListeners() {
  eventManager.emit(DOORS_OPEN_CHANGED_EVENT, areDoorsOpen);
}

function getAllCabinetInstances() {
  const loader = SqftStore.viewGL &&
    SqftStore.viewGL.modelsLoader &&
    SqftStore.viewGL.modelsLoader.loaders &&
    SqftStore.viewGL.modelsLoader.loaders.cabinets &&
    SqftStore.viewGL.modelsLoader.loaders.cabinets.loader;
  if (!loader) return [];
  return Array.from(loader.instances.values());
}

// Falls back to name-based detection when applyTallCabinetLayout hasn't run yet
// (e.g. first click right after 3d load with no saved layout).
function isCabinetFrontMesh(child) {
  const tagged = child.userData.tallCabinetSectionRawType;
  if (tagged) return FRONT_RAW_TYPES.has(tagged);
  return /^(Door[ _](Left|Right)(?:\d*|\.\d+)?|Drawer\d*)$/.test(child.name);
}

function collectVisibleCabinetFrontMeshes(instance) {
  const fronts = [];
  const instances = instance ? [instance] : getAllCabinetInstances();

  instances.forEach((inst) => {
    inst.traverse((child) => {
      if (!child.isMesh || !child.visible) return;
      if (!isCabinetFrontMesh(child)) return;
      fronts.push(child);
    });
  });
  return fronts;
}

function ensureMaterialCloned(mesh) {
  if (mesh.userData[MATERIAL_CLONED_TAG]) return;
  if (Array.isArray(mesh.material)) {
    mesh.material = mesh.material.map((m) => m.clone());
  } else if (mesh.material) {
    mesh.material = mesh.material.clone();
  }
  mesh.userData[MATERIAL_CLONED_TAG] = true;
}

function applyMaterialOpacity(mat, opacity) {
  mat.transparent = opacity < 1;
  mat.opacity = opacity;
  mat.needsUpdate = true;
}

function setFrontOpacity(mesh, opacity) {
  ensureMaterialCloned(mesh);
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((m) => applyMaterialOpacity(m, opacity));
  } else if (mesh.material) {
    applyMaterialOpacity(mesh.material, opacity);
  }
}

function getCurrentOpacity(mesh) {
  const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  return mat && typeof mat.opacity === 'number' ? mat.opacity : 1;
}

export function getDoorsOpen() {
  return areDoorsOpen;
}

export function subscribeToDoorsOpen(listener) {
  eventManager.on(DOORS_OPEN_CHANGED_EVENT, listener);

  return () => {
    eventManager.off(DOORS_OPEN_CHANGED_EVENT, listener);
  };
}

// Resets a single cabinet instance's fronts to fully opaque.
// Call before re-applying a layout so fronts don't stay faded after edits.
export function resetDoorsForInstance(instance) {
  instance.traverse((child) => {
    if (!child.isMesh) return;
    if (!isCabinetFrontMesh(child)) return;
    if (!child.userData[MATERIAL_CLONED_TAG]) return;
    setFrontOpacity(child, 1);
  });
}

// Re-applies the current global front visibility to one cabinet after its layout
// has been rebuilt in place.
export function applyDoorStateForInstance(instance) {
  const fronts = collectVisibleCabinetFrontMeshes(instance);
  const opacity = areDoorsOpen ? DOOR_OPEN_OPACITY : 1;
  fronts.forEach((mesh) => setFrontOpacity(mesh, opacity));
}

// Animates all visible front meshes: open=true fades them out, open=false fades them in.
export function animateDoors(open) {
  areDoorsOpen = open;
  notifyDoorsOpenListeners();

  if (currentRafId !== null) {
    cancelAnimationFrame(currentRafId);
    currentRafId = null;
  }

  const fronts = collectVisibleCabinetFrontMeshes();
  if (!fronts.length) return;

  const startOpacity = getCurrentOpacity(fronts[0]);
  const endOpacity = open ? DOOR_OPEN_OPACITY : 1;

  if (Math.abs(endOpacity - startOpacity) < 0.001) return;

  const travelFraction = Math.abs(endOpacity - startOpacity);
  const duration = ANIM_DURATION_MS * travelFraction;
  const startTime = performance.now();

  function tick() {
    const elapsed = performance.now() - startTime;
    const raw = Math.min(elapsed / duration, 1);
    // Ease in-out cubic
    const t = raw < 0.5 ? 4 * raw * raw * raw : 1 - Math.pow(-2 * raw + 2, 3) / 2;
    const opacity = startOpacity + (endOpacity - startOpacity) * t;

    fronts.forEach((mesh) => setFrontOpacity(mesh, opacity));

    if (raw < 1) {
      currentRafId = requestAnimationFrame(tick);
    } else {
      currentRafId = null;
    }
  }

  currentRafId = requestAnimationFrame(tick);
}
